import {ReactElement, useMemo} from 'react';
import {Link} from '@shopify/hydrogen';
import {useQuery as useBackendQuery} from 'react-query';

import {useShopifyProductMetafields} from '../hooks';
import {getProductAggregatedNotes} from '../backendApi';
import {FlattenedProduct} from '../utils/graphql';

const metafieldsKeys = [
  'country',
  'brand',
  'distillery',
  'type',
  'age',
  'volumeMl',
  'abv',
  'cask',
  'filter',
  'series',
  'style',
];

type ProductInfoProps = Required<
  Pick<FlattenedProduct, 'descriptionHtml' | 'metafields'>
>;

export default function ProductInfo({
  descriptionHtml,
  metafields,
}: ProductInfoProps): ReactElement {
  const {metafieldsKeyValue} = useShopifyProductMetafields(metafields);

  const {data: aggregatedNotes} = useBackendQuery(
    'productAggregatedNotes',
    () => getProductAggregatedNotes(metafieldsKeyValue.uuid),
    {
      enabled: !!metafieldsKeyValue.uuid,
      retry: 2,
    },
  );

  const hasTastingNotesToDisplay = useMemo(() => {
    return (
      aggregatedNotes &&
      (aggregatedNotes.aroma_notes.length > 0 ||
        aggregatedNotes.taste_notes.length > 0 ||
        aggregatedNotes.finish_notes.length > 0)
    );
  }, [aggregatedNotes]);

  const hasAtLeastOneMetafield = useMemo(
    () => metafieldsKeys.some((key) => !!metafieldsKeyValue[key]),
    [metafieldsKeyValue],
  );

  if (
    !descriptionHtml &&
    !hasTastingNotesToDisplay &&
    !hasAtLeastOneMetafield
  ) {
    return <></>;
  }

  return (
    <section id="product-details">
      <div className="product-details pt-100 pb-125 mt-75">
        <div className="container">
          <h4 className="title-2">Product details</h4>
          <div className="text-content c-dark">
            {descriptionHtml && (
              <div>
                <div dangerouslySetInnerHTML={{__html: descriptionHtml}} />
              </div>
            )}
            <br />
            {hasTastingNotesToDisplay && (
              <>
                <p>
                  <strong>Tasting notes</strong>
                </p>
                <ul>
                  {aggregatedNotes.aroma_notes.length > 0 && (
                    <li>
                      <strong>Nose: </strong>{' '}
                      {aggregatedNotes.aroma_notes.join(', ')}
                    </li>
                  )}
                  {aggregatedNotes.taste_notes.length > 0 && (
                    <li>
                      <strong>Palate: </strong>{' '}
                      {aggregatedNotes.taste_notes.join(', ')}
                    </li>
                  )}
                  {aggregatedNotes.finish_notes.length > 0 && (
                    <li>
                      <strong>Finish: </strong>{' '}
                      {aggregatedNotes.finish_notes.join(', ')}
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>
          <div className="product-details__table">
            {metafieldsKeyValue.country && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">
                  Country
                </div>
                <Link
                  to={`/shop/?countries=${metafieldsKeyValue.country.value}`}
                  className="product-details__table__item__text"
                >
                  {metafieldsKeyValue.country.label}
                </Link>
              </div>
            )}
            {metafieldsKeyValue.region && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">
                  Region
                </div>
                <div className="product-details__table__item__text">
                  {metafieldsKeyValue.region}
                </div>
              </div>
            )}
            {metafieldsKeyValue.brand && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">Brand</div>
                <Link
                  to={`/shop/?brands=${metafieldsKeyValue.brand}`}
                  className="product-details__table__item__text"
                >
                  {metafieldsKeyValue.brand}
                </Link>
              </div>
            )}
            {metafieldsKeyValue.distillery && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">
                  Distillery
                </div>
                <div className="product-details__table__item__text">
                  {metafieldsKeyValue.distillery}
                </div>
              </div>
            )}
            {metafieldsKeyValue.type && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">Type</div>
                <Link
                  to={`/shop/?types=${metafieldsKeyValue.type}`}
                  className="product-details__table__item__text"
                >
                  {metafieldsKeyValue.type}
                </Link>
              </div>
            )}
            {metafieldsKeyValue.age && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">Age</div>
                <div className="product-details__table__item__text">
                  {metafieldsKeyValue.age} years
                </div>
              </div>
            )}
            {metafieldsKeyValue.volumeMl && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">
                  Volume
                </div>
                <div className="product-details__table__item__text">
                  {metafieldsKeyValue.volumeMl} ml
                </div>
              </div>
            )}
            {metafieldsKeyValue.abv && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">ABV</div>
                <div className="product-details__table__item__text">
                  {metafieldsKeyValue.abv}%
                </div>
              </div>
            )}
            {metafieldsKeyValue.cask && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">Cask</div>
                <div className="product-details__table__item__text">
                  {metafieldsKeyValue.cask}
                </div>
              </div>
            )}
            {metafieldsKeyValue.filter && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">
                  Filtering
                </div>
                <div className="product-details__table__item__text">
                  {metafieldsKeyValue.filter}
                </div>
              </div>
            )}
            {metafieldsKeyValue.series && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">
                  Series
                </div>
                <div className="product-details__table__item__text">
                  {metafieldsKeyValue.series}
                </div>
              </div>
            )}
            {metafieldsKeyValue.style && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">Style</div>
                <Link
                  to={`/shop/?styles=${metafieldsKeyValue.style}`}
                  className="product-details__table__item__text"
                >
                  {metafieldsKeyValue.style}
                </Link>
              </div>
            )}
            {metafieldsKeyValue.peatLevel && (
              <div className="product-details__table__item">
                <div className="product-details__table__item__label">
                  Peat level
                </div>
                <div className="product-details__table__item__text">
                  {metafieldsKeyValue.peatLevel}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
